// GERADO a partir de backend/src/utils/funnelEngine.js — NAO editar a mao.
// (teste backend: funnelEngine.equivalence.test.js falha se divergir)
// Motor de calculo do Funil Projetado (fonte unica no backend).
// Copia ESM para o frontend em frontend/src/utils/funnelEngine.js — teste de equivalencia em
// __tests__/funnelEngine.equivalence.test.js garante que as duas produzem os mesmos numeros.
//
// Funis antigos (agencyShare ausente/null) usam as formulas originais (100% da meta, Math.ceil exato)
// e NAO sao reinterpretados. Funis novos passam agencyShare (padrao 0.75) e seguem a planilha GRUPO GPF.

const DEFAULT_AGENCY_SHARE = 0.75;

const DEFAULT_RATES = {
  leadToCloseRate: 0.054,
  leadToApptRate: 0.30,
  apptToAttendRate: 0.60,
  bigStretchMultiplier: 1.25
};

// Taxas da trilha Recepcao (planilha: T/30%, U/60%, V/30%); nao editaveis nesta etapa.
const RECEPCAO_RATES = { closeFromAttend: 0.30, attendFromAppt: 0.60, apptFromLeads: 0.30 };

// ROUNDUP da planilha. A tolerancia absorve ruido de ponto flutuante (ex.: 27/0.054 = 500.00000000000006).
const roundUp = (x) => Math.ceil(x - 1e-9);

function defaultBusinessDaysForDezenas(year, month) {
  // D1: dias 1 a 10 | D2: 11 a 20 | D3: 21 ate o fim do mes (dias uteis seg-sex, UTC)
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  let d1 = 0;
  let d2 = 0;
  let d3 = 0;
  for (let day = 1; day <= lastDay; day++) {
    const dayOfWeek = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      if (day <= 10) d1++;
      else if (day <= 20) d2++;
      else d3++;
    }
  }
  return [d1, d2, d3];
}

// agencyShare: undefined/null => funil legado (100%, formulas originais)
function computeUnitPlan(metaReceita, ticketMedio, cplMedio, rates, agencyShare) {
  const {
    leadToCloseRate = 0.054,
    leadToApptRate = 0.30,
    apptToAttendRate = 0.60,
  } = rates || {};

  if (agencyShare === undefined || agencyShare === null) {
    const fechamentos = ticketMedio > 0 ? Math.ceil(metaReceita / ticketMedio) : 0;
    const leads = leadToCloseRate > 0 ? Math.ceil(fechamentos / leadToCloseRate) : 0;
    const agendamentos = Math.ceil(leads * leadToApptRate);
    const comparecimentos = Math.ceil(agendamentos * apptToAttendRate);
    return {
      plannedFechamentos: fechamentos,
      plannedLeads: leads,
      plannedAgendamentos: agendamentos,
      plannedComparecimentos: comparecimentos,
      plannedInvestimento: leads * cplMedio
    };
  }

  const share = Number(agencyShare);
  const meta = Number(metaReceita) || 0;
  const ticket = Number(ticketMedio) || 0;
  const metaCrc = meta * share;
  const fechamentos = ticket > 0 ? roundUp(metaCrc / ticket) : 0;
  const leads = leadToCloseRate > 0 ? roundUp(fechamentos / leadToCloseRate) : 0;
  const agendamentos = roundUp(leads * leadToApptRate);
  const comparecimentos = roundUp(agendamentos * apptToAttendRate);
  return {
    plannedFechamentos: fechamentos,
    plannedLeads: leads,
    plannedAgendamentos: agendamentos,
    plannedComparecimentos: comparecimentos,
    plannedInvestimento: leads * (Number(cplMedio) || 0),
    metaCrc,
    metaRecepcao: meta - metaCrc
  };
}

// Trilha Recepcao (resto da meta). ticketRecepcao padrao = ticket da unidade.
function computeRecepcaoPlan(metaReceita, agencyShare, ticketRecepcao) {
  const meta = Number(metaReceita) || 0;
  const share = Number(agencyShare);
  const metaRecepcao = meta - meta * share;
  const ticket = Number(ticketRecepcao) || 0;
  const fechamentos = ticket > 0 ? roundUp(metaRecepcao / ticket) : 0;
  const comparecimentos = roundUp(fechamentos / RECEPCAO_RATES.closeFromAttend);
  const agendamentos = roundUp(comparecimentos / RECEPCAO_RATES.attendFromAppt);
  const leads = roundUp(agendamentos / RECEPCAO_RATES.apptFromLeads);
  return { metaRecepcao, ticketRecepcao: ticket, fechamentos, comparecimentos, agendamentos, leads };
}

// Dezenas: cada metrica e dividida pela fracao de dias uteis (ROUND), D3 = residuo.
// Para comparecimentos isso e exatamente P/Q/R da planilha.
function splitByDezena(plan, dezenaBusinessDays, multiplier = 1.25) {
  const totalDays = dezenaBusinessDays.reduce((a, b) => a + b, 0) || 1;
  const daysD1 = dezenaBusinessDays[0] || 0;
  const daysD2 = dezenaBusinessDays[1] || 0;
  const daysD3 = dezenaBusinessDays[2] || 0;

  const fraction1 = daysD1 / totalDays;
  const fraction2 = daysD2 / totalDays;

  const d1PlannedLeads = Math.round(plan.plannedLeads * fraction1);
  const d1PlannedAgendamentos = Math.round(plan.plannedAgendamentos * fraction1);
  const d1PlannedComparecimentos = Math.round(plan.plannedComparecimentos * fraction1);
  const d1PlannedFechamentos = Math.round(plan.plannedFechamentos * fraction1);
  const d1PlannedInvestimento = plan.plannedInvestimento * fraction1;

  const d2PlannedLeads = Math.round(plan.plannedLeads * fraction2);
  const d2PlannedAgendamentos = Math.round(plan.plannedAgendamentos * fraction2);
  const d2PlannedComparecimentos = Math.round(plan.plannedComparecimentos * fraction2);
  const d2PlannedFechamentos = Math.round(plan.plannedFechamentos * fraction2);
  const d2PlannedInvestimento = plan.plannedInvestimento * fraction2;

  const d3PlannedLeads = plan.plannedLeads - d1PlannedLeads - d2PlannedLeads;
  const d3PlannedAgendamentos = plan.plannedAgendamentos - d1PlannedAgendamentos - d2PlannedAgendamentos;
  const d3PlannedComparecimentos = plan.plannedComparecimentos - d1PlannedComparecimentos - d2PlannedComparecimentos;
  const d3PlannedFechamentos = plan.plannedFechamentos - d1PlannedFechamentos - d2PlannedFechamentos;
  const d3PlannedInvestimento = plan.plannedInvestimento - d1PlannedInvestimento - d2PlannedInvestimento;

  const realized = {
    realizadoLeads: 0,
    realizadoAgendamentos: 0,
    realizadoComparecimentos: 0,
    realizadoFechamentos: 0,
    realizadoInvestimento: 0
  };

  return [
    {
      name: 'D1',
      businessDays: daysD1,
      plannedLeads: d1PlannedLeads,
      plannedAgendamentos: d1PlannedAgendamentos,
      plannedComparecimentos: d1PlannedComparecimentos,
      plannedFechamentos: d1PlannedFechamentos,
      plannedInvestimento: d1PlannedInvestimento,
      bigLeads: Math.round(plan.plannedLeads * fraction1 * multiplier),
      bigAgendamentos: Math.round(plan.plannedAgendamentos * fraction1 * multiplier),
      bigComparecimentos: Math.round(plan.plannedComparecimentos * fraction1 * multiplier),
      bigFechamentos: Math.round(plan.plannedFechamentos * fraction1 * multiplier),
      bigInvestimento: plan.plannedInvestimento * fraction1 * multiplier,
      ...realized
    },
    {
      name: 'D2',
      businessDays: daysD2,
      plannedLeads: d2PlannedLeads,
      plannedAgendamentos: d2PlannedAgendamentos,
      plannedComparecimentos: d2PlannedComparecimentos,
      plannedFechamentos: d2PlannedFechamentos,
      plannedInvestimento: d2PlannedInvestimento,
      bigLeads: Math.round(plan.plannedLeads * fraction2 * multiplier),
      bigAgendamentos: Math.round(plan.plannedAgendamentos * fraction2 * multiplier),
      bigComparecimentos: Math.round(plan.plannedComparecimentos * fraction2 * multiplier),
      bigFechamentos: Math.round(plan.plannedFechamentos * fraction2 * multiplier),
      bigInvestimento: plan.plannedInvestimento * fraction2 * multiplier,
      ...realized
    },
    {
      name: 'D3',
      businessDays: daysD3,
      plannedLeads: d3PlannedLeads,
      plannedAgendamentos: d3PlannedAgendamentos,
      plannedComparecimentos: d3PlannedComparecimentos,
      plannedFechamentos: d3PlannedFechamentos,
      plannedInvestimento: d3PlannedInvestimento,
      bigLeads: Math.round(d3PlannedLeads * multiplier),
      bigAgendamentos: Math.round(d3PlannedAgendamentos * multiplier),
      bigComparecimentos: Math.round(d3PlannedComparecimentos * multiplier),
      bigFechamentos: Math.round(d3PlannedFechamentos * multiplier),
      bigInvestimento: d3PlannedInvestimento * multiplier,
      ...realized
    }
  ];
}

// CPL efetivo conforme o tipo escolhido: fixo (digitado) | anterior (media 3M) | real (mes atual)
function resolveActiveCpl(unit) {
  const cplTipo = unit.cplTipo || 'fixo';
  const cplFixo = unit.cplFixo !== undefined && unit.cplFixo !== null ? Number(unit.cplFixo) : Number(unit.cplMedio || 0);
  const cplAnterior = Number(unit.cplAnterior || 0);
  const rLeads = Number(unit.realizadoLeads || 0);
  const rInv = Number(unit.realizadoInvestimento || 0);
  if (cplTipo === 'anterior') return cplAnterior || cplFixo;
  if (cplTipo === 'real') return rLeads > 0 ? rInv / rLeads : cplFixo;
  return cplFixo;
}

// Totais do grupo (linha de rodape). CPL medio ponderado = investimento / leads.
function computeTotals(units) {
  const t = {
    metaReceita: 0, metaCrc: 0, metaRecepcao: 0,
    fechamentos: 0, leads: 0, agendamentos: 0, comparecimentos: 0, investimento: 0,
    recepFechamentos: 0, recepComparecimentos: 0, recepAgendamentos: 0, recepLeads: 0,
    dezenas: [0, 0, 0]
  };
  units.forEach(u => {
    t.metaReceita += Number(u.metaReceita) || 0;
    t.metaCrc += Number(u.metaCrc) || 0;
    t.metaRecepcao += Number(u.metaRecepcao) || 0;
    t.fechamentos += Number(u.plannedFechamentos) || 0;
    t.leads += Number(u.plannedLeads) || 0;
    t.agendamentos += Number(u.plannedAgendamentos) || 0;
    t.comparecimentos += Number(u.plannedComparecimentos) || 0;
    t.investimento += Number(u.plannedInvestimento) || 0;
    if (u.recepcao) {
      t.recepFechamentos += u.recepcao.fechamentos || 0;
      t.recepComparecimentos += u.recepcao.comparecimentos || 0;
      t.recepAgendamentos += u.recepcao.agendamentos || 0;
      t.recepLeads += u.recepcao.leads || 0;
    }
    (u.dezenaBreakdown || []).forEach((d, i) => { if (i < 3) t.dezenas[i] += Number(d.plannedComparecimentos) || 0; });
  });
  t.cplMedio = t.leads > 0 ? t.investimento / t.leads : 0;
  t.pctInvestReceita = t.metaReceita > 0 ? t.investimento / t.metaReceita : 0;
  return t;
}

// Alertas de sanidade por unidade (nao bloqueiam; so avisam).
function computeAlerts(unit) {
  const alerts = [];
  const cpl = resolveActiveCpl(unit);
  const meta = Number(unit.metaReceita) || 0;
  const invest = Number(unit.plannedInvestimento) || 0;
  const avg3m = Number(unit.cplAnterior) || 0;
  if (!(meta > 0)) alerts.push({ code: 'meta_zero', severity: 'error', message: 'Meta de receita zerada.' });
  if (!(Number(unit.ticketMedio) > 0)) alerts.push({ code: 'ticket_zero', severity: 'error', message: 'Ticket médio zerado: não é possível calcular fechamentos.' });
  if (!(cpl > 0)) alerts.push({ code: 'cpl_zero', severity: 'error', message: 'CPL zerado: investimento projetado ficará em R$ 0.' });
  if (cpl > 0 && avg3m > 0) {
    if (cpl > avg3m * 2) alerts.push({ code: 'cpl_high', severity: 'warn', message: 'CPL acima de 2x a média dos últimos 3 meses.' });
    else if (cpl < avg3m * 0.5) alerts.push({ code: 'cpl_low', severity: 'warn', message: 'CPL abaixo de 0,5x a média dos últimos 3 meses.' });
  }
  if (meta > 0 && invest > meta * 0.3) alerts.push({ code: 'invest_high', severity: 'warn', message: 'Investimento projetado acima de 30% da meta de receita.' });
  if (unit.clinicorpMatched === false) alerts.push({ code: 'clinicorp_unmatched', severity: 'warn', message: 'Unidade não casou com o Clinicorp: meta de receita não veio automática.' });
  return alerts;
}

export {
  DEFAULT_AGENCY_SHARE,
  DEFAULT_RATES,
  RECEPCAO_RATES,
  roundUp,
  defaultBusinessDaysForDezenas,
  computeUnitPlan,
  computeRecepcaoPlan,
  splitByDezena,
  resolveActiveCpl,
  computeTotals,
  computeAlerts
};
